import type { Vec3, WorldMap } from './protocol';

export const SPEED = 4;
export const HALF_WIDTH = 0.3;
export const HEIGHT = 1.8;
export const STEP_SECONDS = 0.05;
const overlap = (v: number, lo: number, hi: number): boolean => v > lo - HALF_WIDTH && v < hi + HALF_WIDTH;
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export function validPosition(p: Vec3, map: WorldMap): boolean {
  if (![p.x, p.y, p.z].every(Number.isFinite) || p.y !== 0) return false;
  if (p.x < map.bounds.min.x + HALF_WIDTH || p.x > map.bounds.max.x - HALF_WIDTH || p.z < map.bounds.min.z + HALF_WIDTH || p.z > map.bounds.max.z - HALF_WIDTH) return false;
  return !map.obstacles.some(b => b.min.y < HEIGHT && b.max.y > 0 && overlap(p.x, b.min.x, b.max.x) && overlap(p.z, b.min.z, b.max.z));
}

/** Reference controller for fixtures/local mode; live prediction is Phase 3. */
export function step(position: Vec3, direction: { x: number; z: number }, dt: number, map: WorldMap): Vec3 {
  const p = { ...(validPosition(position, map) ? position : map.spawn) };
  if (![direction.x, direction.z, dt].every(Number.isFinite) || dt <= 0 || dt > 1) return p;
  const length = Math.max(1, Math.hypot(direction.x, direction.z));
  let x = clamp(p.x + direction.x / length * SPEED * dt, map.bounds.min.x + HALF_WIDTH, map.bounds.max.x - HALF_WIDTH);
  for (const b of map.obstacles) {
    if (b.min.y >= HEIGHT || b.max.y <= 0 || !overlap(p.z, b.min.z, b.max.z)) continue;
    if (x > p.x && p.x <= b.min.x - HALF_WIDTH) x = Math.min(x, b.min.x - HALF_WIDTH);
    if (x < p.x && p.x >= b.max.x + HALF_WIDTH) x = Math.max(x, b.max.x + HALF_WIDTH);
  }
  p.x = x;
  let z = clamp(p.z + direction.z / length * SPEED * dt, map.bounds.min.z + HALF_WIDTH, map.bounds.max.z - HALF_WIDTH);
  for (const b of map.obstacles) {
    if (b.min.y >= HEIGHT || b.max.y <= 0 || !overlap(p.x, b.min.x, b.max.x)) continue;
    if (z > p.z && p.z <= b.min.z - HALF_WIDTH) z = Math.min(z, b.min.z - HALF_WIDTH);
    if (z < p.z && p.z >= b.max.z + HALF_WIDTH) z = Math.max(z, b.max.z + HALF_WIDTH);
  }
  p.z = z;
  return p;
}
